import type { CSSProperties } from 'react';

import { cn } from './cn';

// ============================================================
// Lume / TEACare PWA — LumeMark
// ============================================================
// SVG inline da marca Lume — blob orgânico navy com núcleo radial
// quente (amarelo → laranja → laranja escuro). Reproduz o logo
// oficial em vetor para escalar perfeitamente em qualquer tamanho
// sem custo de bandwidth. Theme-aware: o blob escurece levemente
// no dark mode para manter contraste sobre fundos escuros.
//
// Uso:
//   <LumeMark size={32} />              // ícone do header
//   <LumeMark size={96} animated />     // splash com pulso radial
//   <LumeMark size={64} title="Lume" /> // com texto acessível
//
// `animated` adiciona um halo radial pulsante (2s, low-stim).
// O CSS global em src/index.css desliga em prefers-reduced-motion.
// ============================================================

export interface LumeMarkProps {
  size?: number;
  className?: string;
  /** Halo radial pulsante (apenas no splash/decorativo). */
  animated?: boolean;
  /**
   * Quando definido, o SVG ganha role="img" + <title>. Sem isto,
   * é decorativo (aria-hidden), assumindo que o texto "Lume" do
   * wordmark próximo dá o contexto.
   */
  title?: string;
}

export function LumeMark({ size = 32, className, animated, title }: LumeMarkProps) {
  const labeled = Boolean(title);
  return (
    <span
      data-testid="lume-mark"
      className={cn(
        'lume-mark relative inline-flex shrink-0 items-center justify-center',
        animated && 'lume-mark--animated',
        className,
      )}
      style={{ width: size, height: size } as CSSProperties}
      aria-hidden={labeled ? undefined : true}
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        role={labeled ? 'img' : undefined}
        aria-hidden={labeled ? undefined : true}
        className="relative z-[1] h-full w-full"
      >
        {labeled ? <title>{title}</title> : null}
        <defs>
          {/* Núcleo quente — amarelo central → laranja médio → laranja escuro */}
          <radialGradient id="lume-warm" cx="42%" cy="40%" r="55%">
            <stop offset="0%" stopColor="#FFE082" />
            <stop offset="35%" stopColor="#FFC857" />
            <stop offset="70%" stopColor="#F08C2E" />
            <stop offset="100%" stopColor="#C9621A" />
          </radialGradient>
          {/* Halo de transição warm → navy */}
          <radialGradient id="lume-edge" cx="42%" cy="40%" r="80%">
            <stop offset="55%" stopColor="rgba(201,98,26,0)" />
            <stop offset="78%" stopColor="rgba(70,40,15,0.18)" />
            <stop offset="100%" stopColor="rgba(70,40,15,0.32)" />
          </radialGradient>
          {/* Sombra interna do blob navy */}
          <radialGradient id="lume-navy" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="var(--lume-mark-navy-1, #2B5479)" />
            <stop offset="100%" stopColor="var(--lume-mark-navy-2, #173A5C)" />
          </radialGradient>
        </defs>

        {/* Blob navy orgânico — desenhado para casar com o logo PNG */}
        <path
          d="M50 4
             C72 4 92 18 96 42
             C100 64 88 84 66 93
             C44 99 18 92 8 72
             C-1 54 4 30 22 16
             C30 9 40 4 50 4 Z"
          fill="url(#lume-navy)"
        />

        {/* Núcleo radial quente — sol/aurora */}
        <ellipse cx="42" cy="44" rx="26" ry="24" fill="url(#lume-warm)" />
        <ellipse cx="42" cy="44" rx="26" ry="24" fill="url(#lume-edge)" />
      </svg>
    </span>
  );
}
